import { Button, Card, Col, Collapse, Input, Row, Segmented, Space, Switch, Tooltip } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import {
  CloudSyncOutlined,
  CodeOutlined,
  LinkOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useCallback } from 'react';

import { registryApiBase, toPermalink } from '../utils.js';

type ControlsCardProps = {
  registryUrl: string;
  url: string;
  normalizedUrl: string;
  urlValid: boolean;
  onUrlChange: (value: string) => void;
  onRunScan: () => void;
  onLoadHistory: () => void;
  onResetResult: () => void;
  readyProxy: boolean | null;
  loadingProxy: boolean;
  onCheckHealth: () => void;
  gpc: boolean;
  onGpcChange: (next: boolean) => void;
  captureScreenshot: boolean;
  onCaptureScreenshotChange: (next: boolean) => void;
  captureTrace: boolean;
  onCaptureTraceChange: (next: boolean) => void;
  messageApi: MessageInstance;
  error: string | null;
};

export function ControlsCard({
  registryUrl,
  url,
  normalizedUrl,
  urlValid,
  onUrlChange,
  onRunScan,
  onLoadHistory,
  onResetResult,
  readyProxy,
  loadingProxy,
  onCheckHealth,
  gpc,
  onGpcChange,
  captureScreenshot,
  onCaptureScreenshotChange,
  captureTrace,
  onCaptureTraceChange,
  messageApi,
  error,
}: ControlsCardProps) {
  const handlePermalinkCopy = useCallback(async () => {
    await navigator.clipboard.writeText(toPermalink(normalizedUrl, gpc));
    messageApi.success('Permalink copied');
  }, [gpc, messageApi, normalizedUrl]);

  const handleCurlCopy = useCallback(async () => {
    const payload = JSON.stringify({
      url: normalizedUrl,
      gpc,
      captureScreenshot,
      captureTrace,
    }).replace(/'/g, "'\\''");
    const command = `curl -sS -X POST '${registryApiBase(registryUrl)}/v1/scans/run' -H 'content-type: application/json' --data-raw '${payload}'`;
    await navigator.clipboard.writeText(command);
    messageApi.success('cURL copied');
  }, [captureScreenshot, captureTrace, gpc, messageApi, normalizedUrl, registryUrl]);

  return (
    <Card
      title="Public Scans"
      extra={
        <Space>
          <Tooltip title="API health via Registry">
            <Button icon={<CloudSyncOutlined />} loading={loadingProxy} onClick={onCheckHealth}>
              {readyProxy ? 'Ready' : 'Via Registry'}
            </Button>
          </Tooltip>
        </Space>
      }
    >
      <Row gutter={12}>
        <Col xs={24} md={14}>
          <Input
            prefix={<LinkOutlined />}
            placeholder="https://example.com"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            onPressEnter={() => void onLoadHistory()}
            allowClear
          />
        </Col>
        <Col xs={24} md={10}>
          <Space>
            <Button
              icon={<SearchOutlined />}
              type="primary"
              onClick={() => void onLoadHistory()}
              disabled={!urlValid}
            >
              Load History
            </Button>
            <Tooltip title="Copy permalink with current URL">
              <Button icon={<LinkOutlined />} onClick={() => void handlePermalinkCopy()} />
            </Tooltip>
            <Button icon={<ReloadOutlined />} onClick={onResetResult}>
              Reset
            </Button>
            <Button type="default" onClick={() => void onRunScan()} disabled={!urlValid}>
              Run Scan
            </Button>
            <Tooltip title="Copy cURL (via Registry)">
              <Button icon={<CodeOutlined />} data-testid="copy-curl" onClick={handleCurlCopy} />
            </Tooltip>
          </Space>
        </Col>
      </Row>
      <Collapse
        size="small"
        items={[
          {
            key: 'adv',
            label: 'Advanced',
            children: (
              <Row gutter={12}>
                <Col xs={24} md={6}>
                  <Space>
                    <span>GPC</span>
                    <Segmented
                      options={[
                        { label: 'Off', value: '0' },
                        { label: 'On', value: '1' },
                      ]}
                      value={gpc ? '1' : '0'}
                      onChange={(value) => onGpcChange(value === '1')}
                    />
                  </Space>
                </Col>
                <Col xs={24} md={8}>
                  <Space>
                    <Switch
                      checked={captureScreenshot}
                      onChange={(checked) => onCaptureScreenshotChange(checked)}
                    />
                    <span>Capture screenshot</span>
                  </Space>
                </Col>
                <Col xs={24} md={8}>
                  <Space>
                    <Switch
                      checked={captureTrace}
                      onChange={(checked) => onCaptureTraceChange(checked)}
                    />
                    <span>Capture trace</span>
                  </Space>
                </Col>
              </Row>
            ),
          },
        ]}
      />
      {error && <div style={{ color: '#c33', marginTop: 8 }}>{error}</div>}
    </Card>
  );
}
